/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import * as React from "react";
import styled, { css } from "styled-components";
import { Link } from "react-router";

import { LIVE, TONE_COLOURS, Tone } from "@src/components/modules/LiveModule/liveUtils";

/*
 * The Ops Console card family (consolidated dashboard): white cards, 1 px slate-200 border,
 * 8 px radius, a header row with the title and a quiet note, small uppercase labels, big numbers.
 */

export const Card = styled.section<{ $tint?: "warning" | null; $pad?: boolean }>`
  background: ${LIVE.surface};
  border: 1px solid ${LIVE.border};
  border-radius: 8px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  ${p =>
    p.$tint === "warning"
      ? css`
          background: ${LIVE.warnSoft};
          border-color: ${LIVE.warnBorder};
        `
      : ""}
  ${p =>
    p.$pad
      ? css`
          padding: 16px 20px;
        `
      : ""}
`;

const HeaderRow = styled.div<{ $tint?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid ${LIVE.border}; // was the amber line under a tinted header; the card outline carries it now
`;
const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 15px;
  line-height: 22px;
  font-weight: 600;
  color: ${LIVE.text};
  flex-grow: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;
const HeaderNote = styled.div`
  font-size: 12px;
  color: ${LIVE.muted};
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
`;

export const CardHeader = (props: {
  title: React.ReactNode;
  note?: React.ReactNode;
  tint?: boolean;
  icon?: React.ReactNode;
}) => (
  <HeaderRow $tint={props.tint}>
    <HeaderTitle>
      {props.icon}
      {props.title}
    </HeaderTitle>
    {props.note ? <HeaderNote>{props.note}</HeaderNote> : null}
  </HeaderRow>
);

export const CardBody = styled.div<{ $flush?: boolean }>`
  padding: ${p => (p.$flush ? "0" : "16px 20px")};
  flex-grow: 1;
  min-width: 0;
`;

export const Label = styled.div`
  font-size: 11px;
  line-height: 16px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${LIVE.muted};
`;

export const Muted = styled.span`
  color: ${LIVE.muted};
`;

export const TextLink = styled(Link)`
  color: ${LIVE.slate};
  font-weight: 600;
  font-size: 13px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover {
    color: ${LIVE.slateDark};
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${LIVE.cyan};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const ChipWrap = styled.span<{ $tone: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 22px;
  padding: 0 9px;
  border-radius: 11px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  background: ${p => TONE_COLOURS[p.$tone].bg};
  color: ${p => TONE_COLOURS[p.$tone].fg};
`;
const ChipDot = styled.span<{ $tone: Tone; $pulse?: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${p => TONE_COLOURS[p.$tone].dot};
  ${p =>
    p.$pulse
      ? css`
          box-shadow: 0 0 0 0 ${TONE_COLOURS[p.$tone].dot};
          animation: liveChipPulse 1.6s ease-out infinite;
          @keyframes liveChipPulse {
            0% {
              box-shadow: 0 0 0 0 rgba(70, 196, 242, 0.7);
            }
            100% {
              box-shadow: 0 0 0 7px rgba(70, 196, 242, 0);
            }
          }
          @media (prefers-reduced-motion: reduce) {
            animation: none;
          }
        `
      : ""}
`;

export const Chip = (props: { tone: Tone; children: React.ReactNode; pulse?: boolean; title?: string }) => (
  <ChipWrap $tone={props.tone} title={props.title}>
    <ChipDot $tone={props.tone} $pulse={props.pulse} />
    {props.children}
  </ChipWrap>
);

/* KPI tile: small uppercase label, big number, a line under it, optional sparkline. */
const KpiWrap = styled.div<{ $tone?: "warning" | "danger" | null; $link?: boolean }>`
  border: 1px solid ${LIVE.border};
  border-radius: 8px;
  background: ${LIVE.surface};
  padding: 14px 16px 12px 16px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  position: relative;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s ease;
  ${p =>
    p.$tone === "warning"
      ? css`
          background: ${LIVE.warnSoft};
          border-color: ${LIVE.warnBorder};
        `
      : p.$tone === "danger"
        ? css`
            /* Was a red-filled tile; a failure is one small mark now (UI spec 8.1): the value. */
            border-color: ${LIVE.border};
          `
        : ""}
  ${p =>
    p.$link
      ? css`
          cursor: pointer;
          &:hover {
            border-color: ${LIVE.slate};
          }
        `
      : ""}
`;
const KpiTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;
const KpiValue = styled.div<{ $tone?: "warning" | "danger" | null }>`
  font-size: 26px;
  line-height: 34px;
  font-weight: 700;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: ${p => (p.$tone === "danger" ? LIVE.danger : p.$tone === "warning" ? LIVE.warningDeep : LIVE.text)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 4px;
  span.unit {
    font-size: 14px;
    font-weight: 600;
    color: ${LIVE.muted};
    margin-left: 4px;
    letter-spacing: 0;
  }
`;
const KpiSub = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: ${LIVE.muted};
  min-height: 18px;
  b {
    color: ${LIVE.textSecondary};
    font-weight: 600;
  }
`;
const KpiSpark = styled.div`
  margin-top: 8px;
  height: 28px;
`;

export type KpiProps = {
  label: string;
  value: React.ReactNode;
  unit?: string;
  sub?: React.ReactNode;
  icon?: React.ReactNode;
  tone?: "warning" | "danger" | null;
  to?: string;
  spark?: React.ReactNode;
  loading?: boolean;
};

export const Kpi = (props: KpiProps) => {
  const body = (
    <>
      <KpiTop>
        <Label>{props.label}</Label>
        {props.icon}
      </KpiTop>
      <KpiValue $tone={props.tone}>
        {props.loading ? <Muted>—</Muted> : props.value}
        {props.unit && !props.loading ? <span className="unit">{props.unit}</span> : null}
      </KpiValue>
      <KpiSub>{props.loading ? "Loading…" : props.sub}</KpiSub>
      {props.spark ? <KpiSpark>{props.spark}</KpiSpark> : null}
    </>
  );
  if (props.to) {
    return (
      <KpiWrap as={Link as any} to={props.to} $tone={props.tone} $link>
        {body}
      </KpiWrap>
    );
  }
  return <KpiWrap $tone={props.tone}>{body}</KpiWrap>;
};

export const KpiGrid = styled.div<{ $min?: number }>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${p => p.$min || 180}px, 1fr));
  gap: 12px;
`;

/* The overall bar: a slate-50 track, a cyan fill with a slow sheen while it moves. */
const Track = styled.div<{ $h: number }>`
  position: relative;
  height: ${p => p.$h}px;
  border-radius: ${p => p.$h / 2}px;
  background: ${LIVE.track};
  overflow: hidden;
`;
const Fill = styled.div<{ $colour: string; $moving: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-radius: inherit;
  background: ${p => p.$colour};
  transition: width 0.9s cubic-bezier(0.3, 1, 0.4, 1);
  overflow: hidden;
  ${p =>
    p.$moving
      ? css`
          &::after {
            content: "";
            position: absolute;
            inset: 0;
            background: linear-gradient(
              100deg,
              rgba(255, 255, 255, 0) 0%,
              rgba(255, 255, 255, 0.45) 45%,
              rgba(255, 255, 255, 0) 70%
            );
            transform: translateX(-100%);
            animation: liveBarSheen 2.2s ease-in-out infinite;
          }
          @keyframes liveBarSheen {
            to {
              transform: translateX(100%);
            }
          }
          @media (prefers-reduced-motion: reduce) {
            &::after {
              animation: none;
              display: none;
            }
          }
        `
      : ""}
`;

export const Bar = (props: {
  percent: number;
  tone?: "info" | "success" | "danger" | "neutral" | "brand";
  height?: number;
  moving?: boolean;
  label?: string;
}) => {
  const tone = props.tone || "info";
  const colour =
    tone === "success"
      ? LIVE.slate // was a green gradient (UI spec 8.1)
      : tone === "danger"
        ? LIVE.slateDeep // was LIVE.danger: never a whole red bar (UI spec 8.1)
        : tone === "neutral"
          ? LIVE.faint
          : tone === "brand"
            ? LIVE.slate
            : `linear-gradient(90deg, ${LIVE.cyanDeep}, ${LIVE.cyan})`;
  const p = Math.max(0, Math.min(100, props.percent || 0));
  return (
    <Track
      $h={props.height || 8}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(p)}
      aria-label={props.label}
    >
      <Fill $colour={colour} $moving={Boolean(props.moving)} style={{ width: `${p}%` }} />
    </Track>
  );
};

export const EmptyNote = styled.div`
  padding: 20px;
  color: ${LIVE.muted};
  font-size: 13px;
  text-align: center;
`;
